import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { Heart, LogIn, LogOut, Plus, Trash2, Edit, X, Users, MessageSquare, PawPrint, BarChart3, Sparkles } from "lucide-react";
import CloudinaryUpload from "@/components/CloudinaryUpload";

export default function Admin() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("dogs");
  const navigate = useNavigate();

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes("session_id=")) {
      const params = new URLSearchParams(window.location.hash.slice(1));
      const sid = params.get("session_id");
      if (sid) {
        api.post("/auth/session", { session_id: sid })
          .then((r) => {
            setUser(r.data);
            setLoading(false);
            window.history.replaceState({}, "", "/admin");
          })
          .catch(() => setLoading(false));
        return;
      }
    }
    api.get("/auth/me")
      .then((r) => { setUser(r.data); setLoading(false); })
      .catch(() => { setUser(null); setLoading(false); });
  }, []);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
    navigate("/");
  };

  if (loading) {
    return <div className="min-h-[70vh] flex items-center justify-center text-lg" style={{ color: "#0E5C6E" }}>Loading…</div>;
  }

  if (!user) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="admin-login">
        <div className="max-w-md w-full text-center p-10 rounded-3xl" style={{ backgroundColor: "#F7ECD9" }}>
          <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: "#0E5C6E" }}>
            <Heart size={26} color="white" fill="white" />
          </div>
          <h1 className="font-serif text-3xl mb-2" style={{ color: "#0E5C6E" }}>Admin Access</h1>
          <p className="mb-8 text-sm" style={{ color: "#2D2B2A" }}>
            Sign in with your Google account to manage adoptable dogs and applications.
          </p>
          <button className="btn-primary w-full justify-center" onClick={login} data-testid="admin-google-login">
            <LogIn size={16} /> Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  if (!user.is_admin) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center p-10 rounded-3xl" style={{ backgroundColor: "#F7ECD9" }}>
          <h1 className="font-serif text-3xl mb-3" style={{ color: "#0E5C6E" }}>Access denied</h1>
          <p className="mb-6 text-sm" style={{ color: "#2D2B2A" }}>Your account ({user.email}) doesn't have admin access.</p>
          <button className="btn-outline" onClick={logout} data-testid="admin-logout-noaccess">Sign out</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh]" style={{ backgroundColor: "#F7ECD9" }} data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em]" style={{ color: "#E27E4E" }}>Admin Dashboard</div>
            <h1 className="font-serif text-4xl" style={{ color: "#0E5C6E" }}>Aloha, {user.name.split(" ")[0]}.</h1>
          </div>
          <div className="flex items-center gap-3">
            {user.picture && <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />}
            <button className="btn-outline" onClick={logout} data-testid="admin-logout">
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-8 flex-wrap">
          <TabBtn active={tab === "dogs"} onClick={() => setTab("dogs")} icon={<PawPrint size={14} />} label="Dogs" testId="admin-tab-dogs" />
          <TabBtn active={tab === "impact"} onClick={() => setTab("impact")} icon={<BarChart3 size={14} />} label="Impact Stats" testId="admin-tab-impact" />
          <TabBtn active={tab === "sponsors"} onClick={() => setTab("sponsors")} icon={<Sparkles size={14} />} label="Sponsors" testId="admin-tab-sponsors" />
          <TabBtn active={tab === "adoption"} onClick={() => setTab("adoption")} icon={<Heart size={14} />} label="Adoption Inquiries" testId="admin-tab-adoption" />
          <TabBtn active={tab === "volunteers"} onClick={() => setTab("volunteers")} icon={<Users size={14} />} label="Volunteers" testId="admin-tab-volunteers" />
          <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} icon={<MessageSquare size={14} />} label="Messages" testId="admin-tab-messages" />
        </div>

        {tab === "dogs" && <DogsPanel />}
        {tab === "impact" && <ImpactPanel />}
        {tab === "sponsors" && <SponsorsPanel />}
        {tab === "adoption" && <ListPanel endpoint="/admin/adoption-inquiries" fields={ADOPTION_FIELDS} testId="admin-list-adoption" />}
        {tab === "volunteers" && <ListPanel endpoint="/admin/volunteer-applications" fields={VOLUNTEER_FIELDS} testId="admin-list-volunteers" />}
        {tab === "messages" && <ListPanel endpoint="/admin/contact-messages" fields={MESSAGE_FIELDS} testId="admin-list-messages" />}
      </div>
    </div>
  );
}

const ADOPTION_FIELDS = ["created_at", "dog_name", "full_name", "email", "phone", "reason"];
const VOLUNTEER_FIELDS = ["created_at", "full_name", "email", "role", "availability", "location"];
const MESSAGE_FIELDS = ["created_at", "full_name", "email", "subject", "message"];

function TabBtn({ active, onClick, icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      className="px-4 py-2 rounded-full text-sm flex items-center gap-2"
      style={{ backgroundColor: active ? "#0E5C6E" : "white", color: active ? "white" : "#0E5C6E", border: "1px solid #EADCC5" }}
      data-testid={testId}
    >
      {icon} {label}
    </button>
  );
}

const emptyDog = {
  name: "", breed: "", age: "", sex: "Male", size: "Medium",
  story: "", image_url: "", location: "Murrieta, CA", status: "available",
  personality: [], video_url: "", good_with_kids: true, good_with_dogs: true, house_trained: true,
  special_needs: "", zeffy_url: "", featured: false, shop_ready: false,
};

function DogsPanel() {
  const [dogs, setDogs] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/dogs").then((r) => setDogs(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm("Delete this dog?")) return;
    await api.delete(`/admin/dogs/${id}`);
    load();
  };

  return (
    <div>
      <button className="btn-primary mb-6" onClick={() => setEditing(emptyDog)} data-testid="admin-add-dog">
        <Plus size={16} /> Add a dog
      </button>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {dogs.map((d) => (
          <div key={d.id} className="bg-white rounded-2xl overflow-hidden border" style={{ borderColor: "#EADCC5" }}>
            <img src={d.image_url} alt={d.name} className="w-full aspect-video object-cover" />
            <div className="p-5">
              <div className="flex items-center justify-between mb-1">
                <div className="font-serif text-2xl" style={{ color: "#0E5C6E" }}>{d.name}</div>
                <span className={`badge badge-${d.featured ? "urgent" : d.status}`}>{d.featured ? "Urgent" : d.status}</span>
              </div>
              <div className="text-sm mb-4" style={{ color: "#6B655E" }}>{d.breed} • {d.age}</div>
              <div className="flex gap-2">
                <button className="btn-outline" style={{ padding: "0.5rem 1rem", fontSize: "0.8rem" }} onClick={() => setEditing(d)} data-testid={`admin-edit-${d.name.toLowerCase()}`}>
                  <Edit size={14} /> Edit
                </button>
                <button onClick={() => del(d.id)} className="btn-outline" style={{ padding: "0.5rem 1rem", fontSize: "0.8rem", borderColor: "#E27E4E", color: "#E27E4E" }} data-testid={`admin-delete-${d.name.toLowerCase()}`}>
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && <DogFormModal dog={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

const IMPACT_FIELDS = [
  { key: "dogs_rescued", label: "Dogs Rescued", help: "Total number of dogs saved since 2024" },
  { key: "surgeries_funded", label: "Surgeries Funded", help: "Eye, spay/neuter, life-saving procedures" },
  { key: "forever_families", label: "Forever Families", help: "Successful adoption matches" },
  { key: "donations_raised", label: "Donations Raised ($)", help: "Optional USD total. Set 0 to hide from Home page." },
];

function ImpactPanel() {
  const [form, setForm] = useState({ dogs_rescued: 0, surgeries_funded: 0, forever_families: 0, donations_raised: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get("/stats")
      .then((r) => setForm({
        dogs_rescued: r.data?.impact?.dogs_rescued || 0,
        surgeries_funded: r.data?.impact?.surgeries_funded || 0,
        forever_families: r.data?.impact?.forever_families || 0,
        donations_raised: r.data?.impact?.donations_raised || 0,
      }))
      .catch((err) => logError("[Admin/Impact] load failed:", err))
      .finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatus(null);
    try {
      await api.put("/admin/stats", form);
      setStatus({ ok: true, msg: "Saved. The Home page will show these numbers on next load." });
    } catch (err) {
      logError("[Admin/Impact] save failed:", err);
      setStatus({ ok: false, msg: "Could not save. Check console." });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ color: "#6B655E" }}>Loading impact stats…</div>;

  return (
    <div className="max-w-2xl" data-testid="admin-impact-panel">
      <div className="mb-6">
        <h2 className="font-serif text-2xl mb-1" style={{ color: "#0E5C6E" }}>Impact Counter</h2>
        <p className="text-sm" style={{ color: "#6B655E" }}>
          These numbers appear on the Home page. Set a field to <strong>0</strong> to hide that specific stat.
          If all four are 0, the whole section is hidden.
        </p>
      </div>

      <form onSubmit={save} className="space-y-5 bg-white p-6 rounded-2xl border" style={{ borderColor: "#EADCC5" }}>
        {IMPACT_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "#E27E4E" }}>
              {f.label}
            </label>
            <input
              type="number"
              min="0"
              value={form[f.key]}
              onChange={(e) => setForm({ ...form, [f.key]: parseInt(e.target.value, 10) || 0 })}
              className="w-full px-4 py-3 rounded-lg border text-lg font-serif"
              style={{ borderColor: "#EADCC5", color: "#0E5C6E" }}
              data-testid={`admin-impact-${f.key}`}
            />
            {f.help && <div className="text-xs mt-1" style={{ color: "#6B655E" }}>{f.help}</div>}
          </div>
        ))}

        <button type="submit" disabled={saving} className="btn-donate disabled:opacity-50" data-testid="admin-impact-save">
          {saving ? "Saving…" : "Save impact stats"}
        </button>

        {status && (
          <div className="text-sm" style={{ color: status.ok ? "#0E5C6E" : "#C24141" }} data-testid="admin-impact-status">
            {status.msg}
          </div>
        )}
      </form>
    </div>
  );
}

function SponsorsPanel() {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: "", tier_amount: "", dog_name: "", message: "", public: true, month: currentMonth(),
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/sponsors");
      setSponsors(data || []);
    } catch (err) {
      logError("[Admin/Sponsors] load failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form, tier_amount: form.tier_amount ? parseInt(form.tier_amount, 10) : null };
      await api.post("/admin/sponsors", payload);
      setForm({ name: "", tier_amount: "", dog_name: "", message: "", public: true, month: currentMonth() });
      await load();
    } catch (err) {
      logError("[Admin/Sponsors] add failed:", err);
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this sponsor?")) return;
    try {
      await api.delete(`/admin/sponsors/${id}`);
      await load();
    } catch (err) { logError("[Admin/Sponsors] delete failed:", err); }
  };

  const togglePublic = async (s) => {
    try {
      await api.put(`/admin/sponsors/${s.id}`, { public: !s.public });
      await load();
    } catch (err) { logError("[Admin/Sponsors] toggle failed:", err); }
  };

  if (loading) return <div style={{ color: "#6B655E" }}>Loading sponsors…</div>;

  return (
    <div data-testid="admin-sponsors-panel">
      <div className="mb-6">
        <h2 className="font-serif text-2xl mb-1" style={{ color: "#0E5C6E" }}>Sponsors</h2>
        <p className="text-sm" style={{ color: "#6B655E" }}>
          When a supporter starts a monthly Zeffy sponsorship, add them here with their permission. Public sponsors rotate on the Home page.
        </p>
      </div>

      {/* Add form */}
      <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-6 gap-3 bg-white p-5 rounded-2xl border mb-8" style={{ borderColor: "#EADCC5" }}>
        <input type="text" required placeholder="Name (e.g., Malia K.)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="form-input md:col-span-2" data-testid="admin-sponsor-name" />
        <input type="number" min="0" placeholder="$/mo (opt)" value={form.tier_amount} onChange={(e) => setForm({ ...form, tier_amount: e.target.value })} className="form-input" data-testid="admin-sponsor-amount" />
        <input type="text" placeholder="Dog / Fund (opt)" value={form.dog_name} onChange={(e) => setForm({ ...form, dog_name: e.target.value })} className="form-input" data-testid="admin-sponsor-dog" />
        <input type="text" placeholder="YYYY-MM" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className="form-input" data-testid="admin-sponsor-month" />
        <label className="form-input flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.public} onChange={(e) => setForm({ ...form, public: e.target.checked })} data-testid="admin-sponsor-public" /> Public
        </label>
        <textarea rows="2" placeholder="Optional short message from sponsor (with their permission)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="form-input md:col-span-6" data-testid="admin-sponsor-message" />
        <button type="submit" disabled={saving} className="btn-donate md:col-span-6 justify-center" data-testid="admin-sponsor-submit">
          <Plus size={16} /> {saving ? "Adding…" : "Add sponsor"}
        </button>
      </form>

      {/* List */}
      {sponsors.length === 0 ? (
        <div className="text-center py-10 rounded-2xl" style={{ backgroundColor: "#F7ECD9", color: "#6B655E" }}>
          No sponsors yet. Add your first monthly sponsor above.
        </div>
      ) : (
        <div className="space-y-3">
          {sponsors.map((s) => (
            <div key={s.id} className="bg-white p-4 rounded-2xl border flex flex-wrap items-center gap-4" style={{ borderColor: "#EADCC5" }} data-testid={`admin-sponsor-row-${s.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="font-serif text-lg" style={{ color: "#0E5C6E" }}>{s.name}</div>
                <div className="text-xs" style={{ color: "#6B655E" }}>
                  {[s.tier_amount ? `$${s.tier_amount}/mo` : null, s.dog_name, s.month].filter(Boolean).join(" • ")}
                </div>
                {s.message ? <div className="text-sm mt-2 italic" style={{ color: "#2D2B2A" }}>"{s.message}"</div> : null}
              </div>
              <button onClick={() => togglePublic(s)} className="text-xs px-3 py-1.5 rounded-full border" style={{ borderColor: s.public ? "#E27E4E" : "#EADCC5", color: s.public ? "#E27E4E" : "#6B655E" }} data-testid={`admin-sponsor-toggle-${s.id}`}>
                {s.public ? "Public" : "Hidden"}
              </button>
              <button onClick={() => del(s.id)} aria-label="Delete sponsor" className="text-xs p-2 rounded-full" style={{ color: "#C24141" }} data-testid={`admin-sponsor-delete-${s.id}`}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}



function DogFormModal({ dog, onClose, onSaved }) {
  const [form, setForm] = useState({
    ...emptyDog, ...dog,
    personality: Array.isArray(dog.personality) ? dog.personality.join(", ") : "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      personality: form.personality.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (dog.id) {
        await api.put(`/admin/dogs/${dog.id}`, payload);
      } else {
        await api.post("/admin/dogs", payload);
      }
      onSaved();
    } catch (err) {
      alert("Save failed: " + (err.response?.data?.detail || err.message));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="admin-dog-modal">
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl p-8 max-w-2xl w-full my-8 relative" data-testid="admin-dog-form">
        <button type="button" onClick={onClose} className="absolute top-4 right-4"><X /></button>
        <h2 className="font-serif text-3xl mb-6" style={{ color: "#0E5C6E" }}>{dog.id ? "Edit" : "Add"} dog</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <input required placeholder="Name" className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-form-name" />
          <input required placeholder="Breed" className="form-input" value={form.breed} onChange={(e) => setForm({ ...form, breed: e.target.value })} data-testid="admin-form-breed" />
          <input required placeholder="Age (e.g. 2 years)" className="form-input" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} />
          <select className="form-input" value={form.sex} onChange={(e) => setForm({ ...form, sex: e.target.value })}>
            <option>Male</option><option>Female</option>
          </select>
          <select className="form-input" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })}>
            <option>Small</option><option>Medium</option><option>Large</option>
          </select>
          <select className="form-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="admin-form-status">
            <option value="available">Available</option>
            <option value="pending">Pending</option>
            <option value="adopted">Adopted</option>
          </select>
        </div>
        <input placeholder="Photo URL" className="form-input mt-4 hidden" required value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="admin-form-image" />
        <div className="mt-4">
          <CloudinaryUpload value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} resourceType="image" label="Main photo" />
        </div>
        <div className="mt-4">
          <CloudinaryUpload value={form.video_url} onChange={(url) => setForm({ ...form, video_url: url })} resourceType="video" label="Video (optional)" />
        </div>
        <textarea rows="4" required placeholder="Story" className="form-input mt-4" value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} data-testid="admin-form-story" />
        <input placeholder="Personality (comma separated)" className="form-input mt-4" value={form.personality} onChange={(e) => setForm({ ...form, personality: e.target.value })} />
        <input placeholder="Special needs (optional)" className="form-input mt-4" value={form.special_needs || ""} onChange={(e) => setForm({ ...form, special_needs: e.target.value })} />
        <input placeholder="Zeffy campaign URL (optional)" className="form-input mt-4" value={form.zeffy_url || ""} onChange={(e) => setForm({ ...form, zeffy_url: e.target.value })} />
        <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
          <label className="form-input flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.good_with_kids} onChange={(e) => setForm({ ...form, good_with_kids: e.target.checked })} /> Good with kids
          </label>
          <label className="form-input flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.good_with_dogs} onChange={(e) => setForm({ ...form, good_with_dogs: e.target.checked })} /> Good with dogs
          </label>
          <label className="form-input flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.house_trained} onChange={(e) => setForm({ ...form, house_trained: e.target.checked })} /> House trained
          </label>
          <label className="form-input flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="admin-form-featured" /> Featured / Urgent
          </label>
          <label className="form-input flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.shop_ready} onChange={(e) => setForm({ ...form, shop_ready: e.target.checked })} data-testid="admin-form-shop-ready" /> Shop collection is live
          </label>
        </div>
        <button disabled={saving} type="submit" className="btn-primary w-full justify-center mt-6" data-testid="admin-form-submit">
          {saving ? "Saving…" : dog.id ? "Save changes" : "Create dog"}
        </button>
      </form>
    </div>
  );
}

function ListPanel({ endpoint, fields, testId }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(endpoint).then((r) => setItems(r.data)); }, [endpoint]);
  if (items.length === 0) {
    return <div className="p-8 bg-white rounded-2xl text-center" style={{ color: "#6B655E" }} data-testid={testId}>No entries yet.</div>;
  }
  return (
    <div className="bg-white rounded-2xl overflow-hidden border" style={{ borderColor: "#EADCC5" }} data-testid={testId}>
      <table className="w-full text-sm">
        <thead style={{ backgroundColor: "#F7ECD9" }}>
          <tr>
            {fields.map((f) => <th key={f} className="text-left p-4 uppercase text-xs tracking-wider" style={{ color: "#0E5C6E" }}>{f.replace(/_/g, " ")}</th>)}
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={it.id || i} className="border-t" style={{ borderColor: "#EADCC5" }}>
              {fields.map((f) => <td key={f} className="p-4 align-top" style={{ color: "#2D2B2A" }}>{String(it[f] ?? "—").slice(0, 200)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
